import type { BomWoodLine, BomHardwareLine } from '@/services/boms/types'

/**
 * Parse tab-delimited and newline-delimited clipboard text (e.g. copied from Excel / Google Sheets).
 */
export function parseClipboardData(text: string): string[][] {
  if (!text || !text.trim()) return []
  const rawRows = text.trim().split(/\r?\n/)
  return rawRows.map((row) => row.split('\t').map((cell) => cell.trim()))
}

/**
 * Map 2D string array to Wood line objects.
 * Expected columns roughly matching:
 * [Tên chi tiết, Loại gỗ, Dày, Rộng, Dài, Mộng, SL/SP, Veneer, Ghi chú]
 */
export function mapPastedRowsToWoodLines(rows: string[][]): Partial<BomWoodLine>[] {
  return rows.map((cols, idx) => {
    const partName = cols[0] || `Chi tiết dán ${idx + 1}`
    const woodType = cols[1] || ''
    const t = cols[2] && !Number.isNaN(Number(cols[2])) ? Number(cols[2]) : null
    const w = cols[3] && !Number.isNaN(Number(cols[3])) ? Number(cols[3]) : null
    const l = cols[4] && !Number.isNaN(Number(cols[4])) ? Number(cols[4]) : null
    const tenon = cols[5] || ''
    const qty = cols[6] && !Number.isNaN(Number(cols[6])) && Number(cols[6]) > 0 ? Number(cols[6]) : 1
    const veneer = cols[7] || ''
    const notes = cols[8] || ''

    return {
      id: `wood-pasted-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 6)}`,
      part_name: partName,
      wood_type_raw: woodType,
      dims_finished_t: t,
      dims_finished_w: w,
      dims_finished_l: l,
      tenon,
      qty_per: qty,
      veneer_spec: veneer,
      notes,
    }
  })
}

/**
 * Map 2D string array to Hardware line objects.
 * Expected columns roughly matching:
 * [Tên vật tư, Quy cách, SL/SP, Đơn vị, Màu sắc, Ghi chú]
 */
export function mapPastedRowsToHardwareLines(rows: string[][]): Partial<BomHardwareLine>[] {
  return rows.map((cols, idx) => {
    const nameRaw = cols[0] || `Ngũ kim dán ${idx + 1}`
    const specRaw = cols[1] || ''
    const qty = cols[2] && !Number.isNaN(Number(cols[2])) && Number(cols[2]) > 0 ? Number(cols[2]) : 1
    const uom = cols[3] || 'cái'
    const color = cols[4] || ''
    const notes = cols[5] || ''

    return {
      id: `hw-pasted-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 6)}`,
      name_raw: nameRaw,
      spec_raw: specRaw,
      qty_per: qty,
      uom_hint: uom,
      color_hint: color,
      notes,
    }
  })
}

/**
 * Duplicate row at specified index right below it, generating a new id.
 */
export function duplicateRow<T extends { id?: string }>(rows: T[], index: number): T[] {
  if (index < 0 || index >= rows.length) return rows
  const itemToClone = rows[index]
  const cloned = {
    ...itemToClone,
    id: `dup-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  }
  const result = [...rows]
  result.splice(index + 1, 0, cloned)
  return result
}

/**
 * Delete rows whose indices are in the indicesToDelete set.
 */
export function bulkDeleteRows<T>(rows: T[], indicesToDelete: Set<number>): T[] {
  return rows.filter((_, idx) => !indicesToDelete.has(idx))
}

/**
 * Inline validation for wood lines.
 */
export function validateWoodLine(line: Partial<BomWoodLine>): {
  hasError: boolean
  errors: Record<string, string>
  m3Finished: number | null
} {
  const errors: Record<string, string> = {}

  if (!line.part_name || !line.part_name.trim()) {
    errors.part_name = 'Tên chi tiết không được để trống'
  }

  if (line.qty_per == null || line.qty_per <= 0) {
    errors.qty_per = 'Số lượng phải > 0'
  }

  const t = line.dims_finished_t
  const w = line.dims_finished_w
  const l = line.dims_finished_l

  let m3Finished: number | null = null

  if (t == null || t <= 0 || w == null || w <= 0 || l == null || l <= 0) {
    errors.dims = 'Thiếu kích thước tinh'
  } else {
    const qty = line.qty_per != null && line.qty_per > 0 ? line.qty_per : 1
    m3Finished = (Number(t) * Number(w) * Number(l) * Number(qty)) / 1e9
  }

  return {
    hasError: Object.keys(errors).length > 0,
    errors,
    m3Finished,
  }
}

/**
 * Inline validation for hardware lines.
 */
export function validateHardwareLine(line: Partial<BomHardwareLine>): {
  hasError: boolean
  errors: Record<string, string>
} {
  const errors: Record<string, string> = {}

  if (!line.name_raw || !line.name_raw.trim()) {
    errors.name_raw = 'Tên vật tư không được để trống'
  }

  if (line.qty_per == null || line.qty_per <= 0) {
    errors.qty_per = 'Số lượng phải > 0'
  }

  return {
    hasError: Object.keys(errors).length > 0,
    errors,
  }
}
